import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  TrackByFunction,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isString } from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';

import { SearchBody } from '@assess/shared/data-mart-search';
import { Nullable } from '@assess/shared/type-utilities';

export type displayWithFn = (value: any) => string;

@UntilDestroy()
@Component({
  selector: 'assess-lookup-search',
  templateUrl: './lookup-search.component.html',
  styleUrls: ['./lookup-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LookupSearchComponent implements ControlValueAccessor, AfterContentInit, OnDestroy, OnInit {
  private readonly searchResults = new BehaviorSubject<any[]>([]);

  @Output() readonly selectedChanged = new EventEmitter<any>();

  @Input() searchBodyFilters: Nullable<SearchBody['filters']>;
  @Input() searchInputEmptyMessage!: string;
  @Input() searchInputLabel!: string;
  @Input() searchInputPlaceholder!: string;
  @Input() searchResultDisplayWith!: displayWithFn;
  @Input() searchResultTrackBy!: TrackByFunction<any>;
  @Input() searchWith!: (searchBody: SearchBody<any>) => Observable<any[] | undefined>;
  @Input() selectionDisplayWith!: displayWithFn;

  searchCtrl = new FormControl();
  selectionCtrl = new FormControl();
  value: any | undefined;

  readonly removable = true;
  readonly searchDebounce = 250;
  readonly selectable = false;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly searchResults$ = this.searchResults.asObservable();

  get error() {
    return this.selectionCtrl.errors && this.selectionCtrl.touched
      ? this.selectionCtrl.errors[Object.keys(this.selectionCtrl.errors)[0]]
      : '';
  }

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      // Setting the value accessor directly (instead of using the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterContentInit(): void {
    if (this.ngControl?.control) {
      this.selectionCtrl = this.ngControl.control as FormControl;
    }

    this.selectionCtrl.setValue(this.value);
  }

  ngOnDestroy() {
    this.searchResults.complete();
  }

  ngOnInit() {
    this.searchCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(this.searchDebounce),
        filter(() => this.selectionCtrl.enabled),
        filter((value) => isString(value)),
        switchMap((value) => {
          if (value?.trim()?.length === 0) {
            return of([]);
          }

          return this.searchWith({ query: value, filters: this.searchBodyFilters });
        }),
        tap((searchResults: any[] | undefined) => this.searchResults.next(searchResults ?? []))
      )
      .subscribe();
  }

  onChange: (value: any) => void = (_value: any) => {
    /* nothing */
  };

  onTouched: () => void = () => {
    /* nothing */
  };

  remove(): void {
    this.value = undefined;
    this.clearSearch();
  }

  clearSearch(): void {
    this.selectionCtrl.setValue(null);
    this.value = undefined;
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }
  }

  registerOnChange(fn: LookupSearchComponent['onChange']): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: LookupSearchComponent['onTouched']): void {
    this.onTouched = fn;
  }

  onSelect(value: any) {
    if (!this.selectionCtrl.disabled) {
      this.value = value;
      this.onChange(value);
      this.onTouched();
      this.selectedChanged.next({ ...value });
    }
  }
}
